import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Share2, Download, Loader2, Flame, Award, Star, Trophy } from 'lucide-react';
import { toast } from 'sonner';

function getScoreColor(pct) {
  if (pct >= 90) return '#10B981';
  if (pct >= 70) return '#2563EB';
  if (pct >= 50) return '#FF9100';
  return '#EF4444';
}

function getGrade(pct) {
  if (pct >= 95) return 'A+';
  if (pct >= 90) return 'A';
  if (pct >= 85) return 'A-';
  if (pct >= 80) return 'B+';
  if (pct >= 75) return 'B';
  if (pct >= 70) return 'B-';
  if (pct >= 65) return 'C+';
  if (pct >= 60) return 'C';
  return 'D';
}

export default function ShareReportCard({ report, onClose }) {
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);

  if (!report) return null;
  const { summary, days, week_number, player_name } = report;

  const handleShare = async () => {
    setGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#060913',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));

      if (navigator.share && navigator.canShare?.({ files: [new File([blob], 'report.png', { type: 'image/png' })] })) {
        await navigator.share({
          title: `${player_name}'s Weekly Training Report`,
          text: `Week ${week_number} Score: ${summary.weekly_score}/100 | ${summary.consistency_score}% Consistency`,
          files: [new File([blob], `${player_name.replace(/\s/g, '_')}_Week${week_number}_Report.png`, { type: 'image/png' })],
        });
        toast.success('Report shared!');
      } else {
        // Fallback: download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${player_name.replace(/\s/g, '_')}_Week${week_number}_Report.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Report saved to your device!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Failed to generate report');
      }
    }
    setGenerating(false);
  };

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  return (
    <div className="fixed inset-0 z-50 bg-[#060913]/95 flex items-center justify-center px-4 py-6 overflow-y-auto" data-testid="share-report-overlay">
      <div className="max-w-sm w-full space-y-4">
        {/* The shareable card */}
        <div
          ref={cardRef}
          style={{
            background: 'linear-gradient(180deg, #060913 0%, #0B132B 100%)',
            padding: '24px',
            borderRadius: '16px',
            border: '1px solid #1C2B54',
            fontFamily: "'Barlow Condensed', sans-serif",
          }}
        >
          {/* Header with logo */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img
                src="/logo-main.png"
                alt="Elev802 Connected"
                style={{ height: '40px', width: 'auto', borderRadius: '8px' }}
                crossOrigin="anonymous"
              />
              <div>
                <div style={{ fontSize: '14px', fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.5px' }}>CONNECTED COACHING</div>
                <div style={{ fontSize: '9px', color: '#475569', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>Weekly Training Report</div>
              </div>
            </div>
            <div style={{ fontSize: '10px', color: '#475569', fontWeight: 700 }}>WEEK {week_number}</div>
          </div>

          {/* Player Name */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 900, color: '#FFFFFF', letterSpacing: '-0.5px' }}>{player_name}</div>
          </div>

          {/* Main Score + Consistency */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            {/* Weekly Score */}
            <div style={{
              flex: 1, background: '#0B132B', border: '1px solid #1C2B54', borderRadius: '12px', padding: '16px', textAlign: 'center',
            }}>
              <div style={{ fontSize: '9px', color: '#475569', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '4px' }}>WEEKLY SCORE</div>
              <div style={{ fontSize: '40px', fontWeight: 900, color: getScoreColor(summary.weekly_score), lineHeight: 1 }}>
                {summary.weekly_score}
              </div>
              <div style={{ fontSize: '12px', color: '#475569' }}>/100</div>
              <div style={{
                marginTop: '6px', display: 'inline-block', padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 800,
                background: `${getScoreColor(summary.weekly_score)}20`, color: getScoreColor(summary.weekly_score),
              }}>
                Grade: {getGrade(summary.weekly_score)}
              </div>
            </div>

            {/* Consistency */}
            <div style={{
              flex: 1, background: '#0B132B', border: '1px solid #1C2B54', borderRadius: '12px', padding: '16px', textAlign: 'center',
            }}>
              <div style={{ fontSize: '9px', color: '#475569', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '4px' }}>CONSISTENCY</div>
              <div style={{ fontSize: '40px', fontWeight: 900, color: getScoreColor(summary.consistency_score), lineHeight: 1 }}>
                {summary.consistency_score}%
              </div>
              <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>
                {summary.days_trained}/{summary.total_days} days
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <div style={{ flex: 1, background: '#0B132B', border: '1px solid #1C2B54', borderRadius: '10px', padding: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '8px', color: '#475569', fontWeight: 700, letterSpacing: '1.5px' }}>DRILLS</div>
              <div style={{ fontSize: '18px', fontWeight: 900, color: '#FFFFFF' }}>
                {summary.total_drills_completed}<span style={{ fontSize: '10px', color: '#475569' }}>/{summary.total_drills}</span>
              </div>
            </div>
            <div style={{ flex: 1, background: '#0B132B', border: '1px solid #1C2B54', borderRadius: '10px', padding: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '8px', color: '#475569', fontWeight: 700, letterSpacing: '1.5px' }}>STREAK</div>
              <div style={{ fontSize: '18px', fontWeight: 900, color: '#FF9100', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                <span>🔥</span> {summary.current_streak}
              </div>
            </div>
            {summary.badges?.length > 0 && (
              <div style={{ flex: 1, background: '#0B132B', border: '1px solid #1C2B54', borderRadius: '10px', padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: '8px', color: '#475569', fontWeight: 700, letterSpacing: '1.5px' }}>BADGES</div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginTop: '2px' }}>
                  {summary.badges.map(b => (
                    <span key={b.name} style={{ fontSize: '16px' }}>
                      {b.name === 'Gold' ? '🥇' : b.name === 'Silver' ? '🥈' : '🥉'}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Daily Breakdown Bar Chart */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '9px', color: '#475569', fontWeight: 700, letterSpacing: '2px', marginBottom: '8px' }}>DAILY SCORES</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '60px' }}>
              {days.map((day, i) => {
                const pct = day.score_percentage;
                const h = Math.max(4, (pct / 100) * 56);
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                    <div style={{ fontSize: '9px', fontWeight: 800, color: pct > 0 ? getScoreColor(pct) : '#1C2B54' }}>
                      {pct > 0 ? pct : '—'}
                    </div>
                    <div style={{
                      width: '100%', height: `${h}px`, borderRadius: '4px',
                      background: pct > 0 ? getScoreColor(pct) : '#1C2B54',
                      opacity: pct > 0 ? 1 : 0.3,
                    }} />
                    <div style={{ fontSize: '8px', color: '#475569', fontWeight: 600 }}>
                      {dayNames[i]?.substring(0, 3) || `D${i + 1}`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div style={{
            borderTop: '1px solid #1C2B54', paddingTop: '12px',
          }}>
            {/* Viral rank + join line */}
            {summary.player_rank && (
              <div style={{
                textAlign: 'center', marginBottom: '12px', padding: '8px 12px',
                background: '#2563EB15', borderRadius: '8px', border: '1px solid #2563EB30',
              }}>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#E2E8F0' }}>
                  Your player ranked <span style={{ color: '#00E5FF', fontSize: '14px' }}>#{summary.player_rank}</span> this week
                  {summary.total_players ? ` out of ${summary.total_players} players` : ''}
                </div>
                <div style={{ fontSize: '11px', color: '#2563EB', fontWeight: 700, marginTop: '4px' }}>
                  Join here → connectedcoachingplatform.com
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '8px', color: '#475569', fontWeight: 600, letterSpacing: '1px' }}>
                CONNECTEDCOACHINGPLATFORM.COM
              </div>
              <div style={{ fontSize: '8px', color: '#2563EB', fontWeight: 700 }}>
                #TrainSmarter
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons (outside the card, not captured in image) */}
        <div className="flex gap-3">
          <button
            onClick={handleShare}
            disabled={generating}
            className="flex-1 bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-xl py-4 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50"
            data-testid="share-report-btn"
          >
            {generating ? (
              <><Loader2 size={16} className="animate-spin" /> Generating...</>
            ) : (
              <><Share2 size={16} /> Share Report</>
            )}
          </button>
          <button
            onClick={onClose}
            className="px-6 bg-white border border-[#E2E8F0] text-[#64748B] font-bold uppercase tracking-wide rounded-xl py-4 text-sm"
            data-testid="close-share-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
